/**
 * PodcastDetailView - Podcast detail panel with tabs
 *
 * Single Responsibility: Displays podcast details with tabbed navigation.
 * Orchestrates header, metadata, actions menu, and tab switching.
 * Does NOT handle tab content, data fetching, or list view.
 */
package com.castly.admin.ui.podcasts

import androidx.compose.runtime.*
import com.castly.admin.api.ApiClient
import com.castly.admin.ui.*
import com.castly.admin.ui.components.IconButton
import com.castly.admin.ui.components.ConfirmDialog
import com.castly.admin.ui.icons.IconEdit
import com.castly.admin.ui.icons.IconTrash
import com.castly.admin.ui.icons.IconCheck
import com.castly.admin.ui.icons.IconMenu
import com.castly.admin.ui.icons.IconPlus
import com.castly.admin.ui.icons.IconRss
import com.castly.admin.ui.icons.IconRefreshCw
import com.castly.admin.ui.icons.IconImage
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.LightColors
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import com.castly.shared.dto.*
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun PodcastDetailView(
    podcast: PodcastDto,
    activeTab: DetailTab,
    apiClient: ApiClient,
    onTabChange: (DetailTab) -> Unit,
    onEdit: (PodcastDto) -> Unit,
    onDelete: (PodcastDto) -> Unit,
    onPublish: (PodcastDto) -> Unit,
    onPodcastUpdate: (String) -> Unit,
    onClose: () -> Unit,
    dataTab: @Composable (PodcastDto, ApiClient, () -> Unit) -> Unit,
    episodesTab: @Composable (PodcastDto, ApiClient, () -> Unit) -> Unit,
    tagsTab: @Composable (PodcastDto, ApiClient) -> Unit,
    translationsTab: @Composable (PodcastDto, ApiClient, (String) -> Unit) -> Unit
) {
    val colors = AppTheme.colors
    val userRoleStr = window.localStorage.getItem("user_role") ?: "NONE"
    val canEdit = userRoleStr.uppercase() in setOf("LABEL_EDITOR", "EDITOR", "ADMIN", "SUPER_ADMIN")
    val canDelete = userRoleStr.uppercase() in setOf("ADMIN", "SUPER_ADMIN")

    var showMenu by remember { mutableStateOf(false) }
    var syncing by remember { mutableStateOf(false) }
    var syncError by remember { mutableStateOf<String?>(null) }
    var publishError by remember { mutableStateOf<String?>(null) }
    var showConvertDialog by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    // Image upload state
    var isUploadingImage by remember { mutableStateOf(false) }
    var uploadImageError by remember { mutableStateOf<String?>(null) }

    // Confirm dialog state
    var showDeleteConfirm by remember { mutableStateOf(false) }
    var showRevertImageConfirm by remember { mutableStateOf(false) }

    Div({
        attr("data-testid", "podcast-detail-panel")
        style {
            height(100.percent)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            themeTransition()
        }
    }) {
        // Header with title, image, and actions menu
        Div({
            style {
                padding(Spacing.lg)
                property("border-bottom", "1px solid ${colors.border}")
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                flexShrink(0)
            }
        }) {
            // Left side: Image + Title + Metadata
            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(Spacing.md)
                    alignItems(AlignItems.Start)
                    flex("1")
                }
            }) {
                // Podcast Image with controls
                val displayImageUrl = podcast.coverArtUrl ?: podcast.originalCoverArtUrl
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        property("flex-direction", "column")
                        gap(Spacing.xs)
                    }
                }) {
                    // Image
                    Div({
                        style {
                            position(Position.Relative)
                        }
                    }) {
                        if (displayImageUrl != null) {
                            Img(displayImageUrl) {
                                style {
                                    width(56.px)
                                    height(56.px)
                                    borderRadius(BorderRadius.lg)
                                    property("object-fit", "cover")
                                    border(1.px, LineStyle.Solid, Color(colors.border))
                                }
                            }
                        } else {
                            Div({
                                style {
                                    width(56.px)
                                    height(56.px)
                                    backgroundColor(Color(colors.surfaceHover))
                                    borderRadius(BorderRadius.lg)
                                    display(DisplayStyle.Flex)
                                    alignItems(AlignItems.Center)
                                    justifyContent(JustifyContent.Center)
                                    color(Color(colors.textMuted))
                                    fontSize(10.px)
                                    border(1.px, LineStyle.Solid, Color(colors.border))
                                }
                            }) { Text("No Image") }
                        }

                        // Hidden file input for image upload
                        Input(InputType.File) {
                            id("podcast-cover-upload")
                            attr("accept", "image/*")
                            if (isUploadingImage) disabled()
                            onChange { event ->
                                val input = event.target as org.w3c.dom.HTMLInputElement
                                val file = input.files?.item(0)
                                if (file != null) {
                                    scope.launch {
                                        isUploadingImage = true
                                        uploadImageError = null
                                        try {
                                            apiClient.uploadPodcastCoverImageFile(podcast.id, file.unsafeCast<org.w3c.files.File>())
                                            onPodcastUpdate(podcast.id)
                                            input.value = ""
                                        } catch (e: Exception) {
                                            uploadImageError = e.message ?: "Upload failed"
                                        } finally {
                                            isUploadingImage = false
                                        }
                                    }
                                }
                            }
                            style {
                                display(DisplayStyle.None)
                            }
                        }
                    }

                    // Image controls
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            gap(Spacing.xs)
                        }
                    }) {
                        // Change Image button
                        Button({
                            onClick {
                                kotlinx.browser.document.getElementById("podcast-cover-upload")?.asDynamic()?.click()
                            }
                            if (isUploadingImage) disabled()
                            style {
                                padding(3.px, 6.px)
                                backgroundColor(Color(colors.primary))
                                color(Color("#ffffff"))
                                border(0.px)
                                borderRadius(BorderRadius.sm)
                                fontSize(10.px)
                                fontWeight("500")
                                cursor(if (isUploadingImage) "not-allowed" else "pointer")
                                property("white-space", "nowrap")
                                if (isUploadingImage) opacity(0.6)
                            }
                        }) { Text(if (isUploadingImage) "..." else "Change") }

                        // Revert to Original button
                        if (podcast.coverArtUrl != null && podcast.originalCoverArtUrl != null) {
                            Button({
                                onClick {
                                    showRevertImageConfirm = true
                                }
                                style {
                                    padding(3.px, 6.px)
                                    backgroundColor(Color(colors.warning))
                                    color(Color("#ffffff"))
                                    border(0.px)
                                    borderRadius(BorderRadius.sm)
                                    fontSize(10.px)
                                    fontWeight("500")
                                    cursor("pointer")
                                    property("white-space", "nowrap")
                                }
                            }) { Text("Revert") }
                        }
                    }
                }

                // Title and metadata
                Div({
                    style {
                        flex("1")
                    }
                }) {
                    H3({
                        style {
                            margin(0.px)
                            fontSize(20.px)
                            fontWeight("600")
                        }
                    }) { Text(podcast.title) }

                    // Type badge and other metadata
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(Spacing.sm)
                            marginTop(Spacing.sm)
                            fontSize(Typography.sm)
                        }
                    }) {
                        PodcastTypeChip(podcast.type)

                        if (podcast.isLinked) {
                            StatusBadge(
                                text = "RSS Linked",
                                backgroundColor = if (colors == LightColors) "#dbeafe" else "#0c1929",
                                textColor = if (colors == LightColors) "#1e40af" else "#93c5fd"
                            )
                        }
                    }

                    // Show sync info for linked podcasts
                    if (podcast.isLinked) {
                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                gap(Spacing.sm)
                                marginTop(Spacing.xs)
                                fontSize(Typography.sm)
                                color(Color(colors.textMuted))
                            }
                        }) {
                            podcast.lastSyncedAt?.let {
                                Span { Text("Last sync: ${formatTimestamp(it)}") }
                            }
                            podcast.syncError?.let {
                                Span({
                                    style { color(Color(colors.danger)) }
                                }) { Text("Sync error") }
                            }
                        }
                    }

                    // Show upload error
                    uploadImageError?.let {
                        Div({
                            style {
                                marginTop(Spacing.sm)
                                padding(Spacing.sm)
                                backgroundColor(Color(if (colors == LightColors) "#fee2e2" else "#450a0a"))
                                border(1.px, LineStyle.Solid, Color(if (colors == LightColors) "#fca5a5" else "#991b1b"))
                                borderRadius(BorderRadius.sm)
                                fontSize(Typography.sm)
                                color(Color(colors.danger))
                                themeTransition()
                            }
                        }) {
                            Text(it)
                            Button({
                                onClick { uploadImageError = null }
                                style {
                                    marginLeft(Spacing.sm)
                                    padding(2.px, 6.px)
                                    backgroundColor(Color("transparent"))
                                    border(0.px)
                                    color(Color(colors.danger))
                                    cursor("pointer")
                                    fontSize(Typography.sm)
                                    fontWeight("600")
                                }
                            }) { Text("×") }
                        }
                    }

                    // Show publish error
                    publishError?.let {
                        Div({
                            style {
                                marginTop(Spacing.sm)
                                padding(Spacing.md)
                                backgroundColor(Color(if (colors == LightColors) "#fee2e2" else "#450a0a"))
                                border(1.px, LineStyle.Solid, Color(if (colors == LightColors) "#fca5a5" else "#991b1b"))
                                borderRadius(BorderRadius.sm)
                                fontSize(13.px)
                                color(Color(colors.danger))
                                fontWeight("500")
                                themeTransition()
                            }
                        }) {
                            Text(it)
                            Button({
                                onClick { publishError = null }
                                style {
                                    marginLeft(Spacing.md)
                                    padding(2.px, Spacing.sm)
                                    backgroundColor(Color("transparent"))
                                    border(0.px)
                                    color(Color(colors.danger))
                                    cursor("pointer")
                                    fontSize(Typography.base)
                                    fontWeight("600")
                                }
                            }) { Text("×") }
                        }
                    }
                }
            }

            // Actions area
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(Spacing.sm)
                }
            }) {
                // Close button
                Button({
                    onClick { onClose() }
                    style {
                        padding(Spacing.sm, Spacing.md)
                        backgroundColor(Color(colors.surfaceHover))
                        border(0.px)
                        borderRadius(BorderRadius.md)
                        cursor("pointer")
                        fontSize(Typography.xl)
                        fontWeight("700")
                    }
                }) { Text("×") }

                // Sync button for linked podcasts
                if (podcast.isLinked) {
                    Button({
                        onClick {
                            scope.launch {
                                syncing = true
                                syncError = null
                                try {
                                    apiClient.syncLinkedPodcast(podcast.id)
                                    onPodcastUpdate(podcast.id)
                                } catch (e: Exception) {
                                    syncError = e.message
                                } finally {
                                    syncing = false
                                }
                            }
                        }
                        if (syncing) disabled()
                        style {
                            padding(Spacing.sm, Spacing.md)
                            backgroundColor(Color(colors.primary))
                            color(Color("#ffffff"))
                            border(0.px)
                            borderRadius(BorderRadius.md)
                            cursor(if (syncing) "not-allowed" else "pointer")
                            fontSize(Typography.base)
                            fontWeight("500")
                            property("opacity", if (syncing) "0.7" else "1")
                        }
                    }) { Text(if (syncing) "Syncing..." else "Sync Now") }
                }

                // Three-dots menu
                Div({
                    style {
                        position(Position.Relative)
                    }
                }) {
                    IconButton(
                        icon = { IconMenu() },
                        label = "Actions",
                        onClick = { showMenu = !showMenu }
                    )

                    if (showMenu) {
                        Div({
                            style {
                                position(Position.Absolute)
                                top(100.percent)
                                right(0.px)
                                marginTop(Spacing.xs)
                                backgroundColor(Color(colors.surface))
                                border(1.px, LineStyle.Solid, Color(colors.border))
                                borderRadius(BorderRadius.md)
                                property("box-shadow", "0 4px 6px rgba(0, 0, 0, 0.1)")
                                property("z-index", "10")
                                minWidth(180.px)
                                themeTransition()
                            }
                        }) {
                            // Edit option (only for non-linked podcasts)
                            if (canEdit && !podcast.isLinked) {
                                MenuButton("Edit", onClick = {
                                    showMenu = false
                                    onEdit(podcast)
                                })
                            }

                            // Image management options
                            if (canEdit) {
                                MenuButton("Change Image", onClick = {
                                    showMenu = false
                                    kotlinx.browser.document.getElementById("podcast-cover-upload")?.asDynamic()?.click()
                                })
                            }

                            // Revert to original option
                            if (canEdit && podcast.coverArtUrl != null && podcast.originalCoverArtUrl != null) {
                                MenuButton("Revert to Original", onClick = {
                                    showMenu = false
                                    showRevertImageConfirm = true
                                })
                            }

                            // Convert to Audiobook option (BOOKS type only)
                            if (canEdit && podcast.type == "BOOKS") {
                                MenuButton("Convert to Audiobook", onClick = {
                                    showMenu = false
                                    showConvertDialog = true
                                })
                            }

                            if (canDelete) {
                                MenuButton("Delete", onClick = {
                                    showMenu = false
                                    showDeleteConfirm = true
                                })
                            }
                        }
                    }
                }
            }
        }

        // Tabs
        Div({
            style {
                display(DisplayStyle.Flex)
                property("border-bottom", "1px solid ${colors.border}")
                backgroundColor(Color(colors.surfaceHover))
                flexShrink(0)
                themeTransition()
            }
        }) {
            TabButton("Data", activeTab == DetailTab.DATA) {
                onTabChange(DetailTab.DATA)
            }
            TabButton("Episodes", activeTab == DetailTab.EPISODES) {
                onTabChange(DetailTab.EPISODES)
            }
            TabButton("Tags", activeTab == DetailTab.TAGS) {
                onTabChange(DetailTab.TAGS)
            }
            TabButton("Translations", activeTab == DetailTab.TRANSLATIONS) {
                onTabChange(DetailTab.TRANSLATIONS)
            }
        }

        // Tab content
        Div({
            style {
                flex("1")
                minHeight(0.px)
                property("overflow", "auto")
                padding(Spacing.lg)
            }
        }) {
            when (activeTab) {
                DetailTab.DATA -> dataTab(podcast, apiClient) { onPodcastUpdate(podcast.id) }
                DetailTab.EPISODES -> episodesTab(podcast, apiClient) { onPodcastUpdate(podcast.id) }
                DetailTab.TAGS -> tagsTab(podcast, apiClient)
                DetailTab.TRANSLATIONS -> translationsTab(podcast, apiClient, onPodcastUpdate)
            }
        }
    }

    // Confirm dialog for deleting podcast
    ConfirmDialog(
        isOpen = showDeleteConfirm,
        title = "Delete Podcast",
        message = "Are you sure you want to delete this podcast?",
        confirmLabel = "Delete",
        onConfirm = {
            showDeleteConfirm = false
            onDelete(podcast)
        },
        onCancel = { showDeleteConfirm = false }
    )

    // Confirm dialog for reverting image
    ConfirmDialog(
        isOpen = showRevertImageConfirm,
        title = "Revert Image",
        message = "Revert to the original image?",
        confirmLabel = "Revert",
        isDanger = false,
        onConfirm = {
            showRevertImageConfirm = false
            scope.launch {
                try {
                    apiClient.removePodcastCoverArt(podcast.id)
                    onPodcastUpdate(podcast.id)
                } catch (e: Exception) {
                    uploadImageError = "Failed to revert: ${e.message}"
                }
            }
        },
        onCancel = { showRevertImageConfirm = false }
    )

    // Convert to Audiobook dialog (BOOKS type only)
    ConvertToAudiobookDialog(
        isOpen = showConvertDialog,
        podcast = podcast,
        apiClient = apiClient,
        onConverted = { result ->
            showConvertDialog = false
            onPodcastUpdate(podcast.id)
        },
        onCancel = { showConvertDialog = false }
    )
}

enum class DetailTab {
    DATA,
    EPISODES,
    TAGS,
    TRANSLATIONS
}
